/**
 * Files, videos and links, as the M7 rail shows them.
 *
 * Documents are square tiles typed by their extension. Media (videos, and
 * pictures once side chats take them, creative-os-backend#97) are the one
 * rounded thing in the rail: a thumbnail in the composer, larger once sent.
 * Links become embeds: a video link as media, anything else as a card that
 * names the platform or the site.
 */
import { useState } from "react";
import { AlertCircle, ArrowUpRight, Globe, Play, X } from "lucide-react";
import type { ChatAttachmentItem } from "@/app/routes/dashboard/api/useSessionAttachments";
import type { AttachedVideoLine } from "@/app/routes/dashboard/components/attachedVideos";
import PlatformGlyph from "@/app/routes/dashboard/components/PlatformGlyph";
import { faviconUrl, shortAddress, type RailLink } from "./railLinks";
import { formatDuration } from "./railFormat";

const KIND_LABEL: Record<ChatAttachmentItem["kind"], string> = {
  pdf: "PDF",
  video: "Video",
  image: "Picture",
  spreadsheet: "Spreadsheet",
  text: "Text",
};

function extensionOf(item: ChatAttachmentItem): string {
  const ext = item.fileName.includes(".") ? item.fileName.split(".").pop() ?? "" : "";
  return (ext && ext.length <= 4 ? ext : KIND_LABEL[item.kind]).toUpperCase().slice(0, 4);
}

const isMedia = (item: ChatAttachmentItem) => item.kind === "video" || item.kind === "image";

/** Where a file is on its way: the words under its name. */
function fileMeta(item: ChatAttachmentItem, sent: boolean): string {
  if (item.state === "uploading") {
    return item.progress !== undefined ? `Uploading · ${Math.round(item.progress * 100)}%` : "Uploading";
  }
  if (item.state === "processing") return "Reading";
  if (item.state === "error") return item.error ?? "Couldn't read it";
  const length = formatDuration(item.durationSec);
  if (sent) return length ? `${KIND_LABEL[item.kind]} · ${length}` : KIND_LABEL[item.kind];
  return "Ready";
}

/** The hairline along a tile's foot: how far the upload is, or Reading running. */
function TileBar({ item }: { item: ChatAttachmentItem }) {
  if (item.state === "uploading") {
    return (
      <span
        className="m7r-tile-bar"
        style={{ transform: `scaleX(${Math.max(0.04, Math.min(item.progress ?? 0, 1))})` }}
        aria-hidden
      />
    );
  }
  if (item.state === "processing") return <span className="m7r-tile-bar" data-reading aria-hidden />;
  return null;
}

function RemoveButton({ item, onRemove, round }: { item: ChatAttachmentItem; onRemove: (id: string) => void; round?: boolean }) {
  return (
    <button
      type="button"
      className="m7r-tile-remove"
      data-round={round || undefined}
      aria-label={`Remove ${item.fileName}`}
      title="Remove"
      onClick={() => onRemove(item.id)}
    >
      <X aria-hidden strokeWidth={2} />
    </button>
  );
}

/** A document: square, typed by its extension, its state under its name. */
function DocTile({
  item,
  sent,
  onRemove,
  onRetry,
}: {
  item: ChatAttachmentItem;
  sent: boolean;
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
}) {
  const failed = item.state === "error";
  // An upload cannot be called back mid-flight; the cross waits until it lands.
  const removable = onRemove && item.state !== "uploading";
  return (
    <div className="m7r-doc" data-state={item.state} data-sent={sent || undefined} title={item.error ?? item.fileName}>
      <span className="m7r-doc-type" aria-hidden>
        {failed ? <AlertCircle strokeWidth={1.75} /> : extensionOf(item)}
      </span>
      <span className="m7r-doc-text">
        <span className="m7r-doc-name">{item.fileName}</span>
        <span className="m7r-doc-meta">
          <span>{fileMeta(item, sent)}</span>
          {failed && item.canRetry && onRetry ? (
            <button type="button" className="m7r-link-btn" onClick={() => onRetry(item.id)}>
              Retry
            </button>
          ) : null}
        </span>
      </span>
      {removable ? <RemoveButton item={item} onRemove={onRemove} /> : null}
      <TileBar item={item} />
    </div>
  );
}

/** A video or a picture: rounded, its frame or picture filling it. */
function MediaThumb({
  item,
  size,
  onRemove,
}: {
  item: ChatAttachmentItem;
  size: "small" | "large";
  onRemove?: (id: string) => void;
}) {
  const length = item.kind === "video" ? formatDuration(item.durationSec) : null;
  const label = `${item.fileName}. ${fileMeta(item, size === "large")}`;
  // The picture is one labelled image; the remove control sits beside it, not
  // inside it, so assistive tech still reaches it as a button.
  return (
    <div
      className="m7r-media"
      data-size={size}
      data-kind={item.kind}
      data-poster={item.previewUrl ? true : undefined}
      title={item.fileName}
    >
      <span className="m7r-media-frame" role="img" aria-label={label}>
        {item.previewUrl ? <img src={item.previewUrl} alt="" draggable={false} /> : null}
        {item.kind === "video" ? (
          <span className="m7r-media-play" aria-hidden>
            <Play fill="currentColor" strokeWidth={0} />
          </span>
        ) : null}
        {length && size === "large" ? <span className="m7r-media-length" aria-hidden>{length}</span> : null}
      </span>
      {onRemove && item.state !== "uploading" ? <RemoveButton item={item} onRemove={onRemove} round /> : null}
      <TileBar item={item} />
    </div>
  );
}

/** Files attached since your last message, waiting in the composer. */
export function ComposerFiles({
  items,
  onRemove,
  onRetry,
}: {
  items: ChatAttachmentItem[];
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
}) {
  if (items.length === 0) return null;
  const media = items.filter((item) => isMedia(item) && item.state !== "error");
  const docs = items.filter((item) => !media.includes(item));
  return (
    <div className="m7r-tiles" aria-label="Attached files">
      {media.map((item) => (
        <MediaThumb key={item.id} item={item} size="small" onRemove={onRemove} />
      ))}
      {docs.map((item) => (
        <DocTile key={item.id} item={item} sent={false} onRemove={onRemove} onRetry={onRetry} />
      ))}
    </div>
  );
}

/** The files a message of yours went out with, stacked above its bubble. */
export function SentFiles({
  items,
  onRetry,
}: {
  items: ChatAttachmentItem[];
  onRetry?: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="m7r-sent-files">
      {items.map((item) =>
        isMedia(item) && item.state !== "error" ? (
          <MediaThumb key={item.id} item={item} size="large" />
        ) : (
          <DocTile key={item.id} item={item} sent onRetry={onRetry} />
        ),
      )}
    </div>
  );
}

/* --- links ---------------------------------------------------------------------- */

/** The platform's mark, else the site's icon, else a globe if the icon won't load. */
function LinkMark({ link }: { link: RailLink }) {
  const [broken, setBroken] = useState(false);
  if (link.platform) return <PlatformGlyph platform={link.platform} className="m7r-link-glyph" />;
  if (broken) return <Globe className="m7r-link-glyph" aria-hidden strokeWidth={1.75} />;
  return (
    <img
      className="m7r-link-favicon"
      src={faviconUrl(link.host)}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

/** A link as a compact card: the platform or site, and where it points. */
export function LinkCard({ link }: { link: RailLink }) {
  return (
    <a className="m7r-link-card" href={link.url} target="_blank" rel="noopener noreferrer">
      <span className="m7r-doc-type" aria-hidden>
        <LinkMark link={link} />
      </span>
      <span className="m7r-doc-text">
        <span className="m7r-doc-name">{link.title ?? link.kind}</span>
        <span className="m7r-doc-meta">
          <span>{shortAddress(link)}</span>
        </span>
      </span>
      <ArrowUpRight className="m7r-link-open" aria-hidden strokeWidth={1.75} />
    </a>
  );
}

/** A video link with a poster: media, like a video you attached. */
function LinkVideo({ link }: { link: RailLink }) {
  const [broken, setBroken] = useState(false);
  // A private or removed video serves a grey placeholder or nothing: say less.
  if (broken) return <LinkCard link={link} />;
  return (
    <a className="m7r-link-video" href={link.url} target="_blank" rel="noopener noreferrer">
      <span className="m7r-media" data-size="large" data-kind="video" data-poster>
        <img
          src={link.poster ?? undefined}
          alt=""
          loading="lazy"
          draggable={false}
          onError={() => setBroken(true)}
          onLoad={(e) => {
            // YouTube answers a missing video with a 120x90 grey frame, not an error.
            if (e.currentTarget.naturalWidth <= 120) setBroken(true);
          }}
        />
        <span className="m7r-media-play" aria-hidden>
          <Play fill="currentColor" strokeWidth={0} />
        </span>
      </span>
      <span className="m7r-link-caption">
        {link.platform ? <PlatformGlyph platform={link.platform} className="m7r-link-glyph" /> : null}
        <span className="m7r-doc-text">
          <span className="m7r-doc-name">{link.kind}</span>
          <span className="m7r-doc-meta">
            <span>{shortAddress(link)}</span>
          </span>
        </span>
      </span>
    </a>
  );
}

/** The links a message ended on, or the agent put on their own lines. */
export function LinkEmbeds({ links, side }: { links: RailLink[]; side: "you" | "agent" }) {
  if (links.length === 0) return null;
  return (
    <div className="m7r-links" data-side={side}>
      {links.map((link) =>
        link.video && link.poster ? <LinkVideo key={link.url} link={link} /> : <LinkCard key={link.url} link={link} />,
      )}
    </div>
  );
}

/* --- a video the agent attached from a link ------------------------------------- */

const PORTRAIT_HOSTS = /(^|\.)(tiktok|instagram)\.com$/;

/** The post a video came from, if it is a web address; anything else is dropped. */
function safeSource(url: string | null | undefined): { href: string; host: string } | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return { href: parsed.href, host: parsed.hostname.replace(/^www\./, "") };
  } catch {
    return null;
  }
}

/**
 * Videos the agent attached from links, on its side of the conversation: the
 * post's shape (a reel stands up), a play mark, its length, and where it came from.
 */
export function AgentVideos({
  videos,
  linked,
}: {
  videos: AttachedVideoLine[];
  linked: Map<string, ChatAttachmentItem>;
}) {
  const shown = videos.filter((v) => v.ok);
  if (shown.length === 0) return null;
  return (
    <div className="m7r-agent-videos">
      {shown.map((video) => {
        const item = video.documentId ? linked.get(video.documentId) : undefined;
        const source = safeSource(video.url ?? item?.sourceUrl);
        const length = formatDuration(item?.durationSec);
        const portrait = source ? PORTRAIT_HOSTS.test(source.host) : false;
        return (
          <figure key={video.key} className="m7r-agent-video">
            <span
              className="m7r-media"
              data-size="large"
              data-kind="video"
              data-portrait={portrait || undefined}
              role="img"
              aria-label={`Video: ${video.name}${length ? `, ${length}` : ""}`}
            >
              <span className="m7r-media-play" aria-hidden>
                <Play fill="currentColor" strokeWidth={0} />
              </span>
              {length ? <span className="m7r-media-length" aria-hidden>{length}</span> : null}
            </span>
            <figcaption>
              <span className="m7r-doc-name">{video.name}</span>
              {source ? (
                <a className="m7r-doc-meta" href={source.href} target="_blank" rel="noopener noreferrer">
                  From {source.host}
                </a>
              ) : null}
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}
